/**
 * Extracts a human-readable error message from an Axios API error.
 * Surfaces the API message only for 400 responses with an array or string message
 * (business-rule violations). Falls back to `fallback` for all other shapes.
 */
export function extractApiErrorMessage(error, fallback) {
    const axiosError = error;
    const data = axiosError?.response?.data;
    if (!data)
        return fallback;
    const { statusCode, message } = data;
    if (statusCode === 400 && Array.isArray(message) && message.length > 0) {
        const text = message
            .map((item) => item.error ?? Object.values(item)[0])
            .filter(Boolean)
            .join('. ');
        if (text)
            return text;
    }
    if (statusCode === 400 && typeof message === 'string' && message) {
        return message;
    }
    return fallback;
}
/**
 * Resolves API errors through the i18n contract first, while preserving the
 * legacy message fallback until each screen has migrated off raw API copy.
 */
export function resolveApiErrorText(error, translate, fallback) {
    const axiosError = error;
    const data = axiosError?.response?.data;
    if (data?.translation_key && !isMisMappedNotFoundKey(data)) {
        const translated = translate(data.translation_key, data.variables ?? {});
        if (translated && translated !== data.translation_key)
            return translated;
    }
    return extractApiErrorMessage(error, fallback);
}
/**
 * The legacy API filter infers translation keys by substring, so a 400 such as
 * "El usuario ya existe" arrives as `errors.user.not_found`. A `*.not_found`
 * key is only trustworthy on a 404; otherwise prefer the API's own message.
 */
function isMisMappedNotFoundKey(data) {
    return (typeof data.statusCode === 'number' &&
        data.statusCode !== 404 &&
        /(^|\.)not_found$/.test(data.translation_key ?? ''));
}
